import React, { useEffect, useState, useRef } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Row,
    Label,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import {
    getOfferTermsConditions,
    manageOfferTermsConditions,
    clearOfferTermsConditions,
    addTermToForm,
    updateTermInForm,
    removeTermFromForm,
    clearTermForm,
} from "store/offers/actions"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import * as url from "../../helpers/url_helper"
import TextInput from "components/Common/TextInput"

export default function OfferTermsForm({
    page,
    match,
    history,
    offerType,
    canEdit,
    onTabCompletion,
    activeTab,
    tabId,
    createdOfferID,
    copyFromID,
    fromType,
}) {
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingTermIndex, setEditingTermIndex] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentTerm, setCurrentTerm] = useState({
        termText: "",
    })

    const prevTermCreationSuccess = useRef(null)
    const prevOfferTermsConditionsError = useRef(null)
    const hasFetchedCopyData = useRef(false) // Track if we've fetched data from copied offer

    const dispatch = useDispatch()
    
    const detailID = match?.params?.id && match?.params?.id !== "add" ? match.params.id : null

    const offerID = page === "add" ? createdOfferID : detailID
    
    // For copy functionality, use the source offer ID to load terms data initially,
    // but switch to createdOfferID once the offer is created
    const sourceOfferID = (copyFromID && page === "add" && !createdOfferID) ? copyFromID : offerID

    // Validation schema
    const validationSchema = Yup.object({
        termText: Yup.string()
            .required("Term text is required")
            .max(500, "Term text cannot exceed 500 characters")
            .trim("Term text cannot contain leading or trailing spaces"),
    })

    // Form validation using Formik
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            termText: currentTerm.termText,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleAddTermToForm()
            }
        },
    })

    // Update form values when currentTerm changes
    useEffect(() => {
        validation.setValues({
            termText: currentTerm.termText,
        })
    }, [currentTerm])

    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    const {
        offerTermsConditions,
        offerTermsConditionsError,
        isLoadingOfferTermsConditions,
        termFormData,
        termCreationSuccess,
    } = useSelector(state => ({
        offerTermsConditions: state.offers.offerTermsConditions || [],
        offerTermsConditionsError: state.offers.offerTermsConditionsError,
        isLoadingOfferTermsConditions: state.offers.isLoadingOfferTermsConditions || false,
        termFormData: state.offers.termFormData || [],
        termCreationSuccess: state.offers.termCreationSuccess,
    }))

    // Pre-fetch offer terms when copying (even if tab is not active yet)
    useEffect(() => {
        // When copying, fetch data from source offer immediately
        if (copyFromID && page === "add" && !createdOfferID && !hasFetchedCopyData.current) {
            hasFetchedCopyData.current = true
            dispatch(
                getOfferTermsConditions({
                    headers: {
                        "input-parameters": { offerID: copyFromID },
                    },
                })
            )
        }
    }, [dispatch, copyFromID, page, createdOfferID])

    // Fetch offer terms when tab is active (for detail mode or after save)
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (sourceOfferID && isTabActive && page === "detail") {
            dispatch(
                getOfferTermsConditions({
                    headers: {
                        "input-parameters": { offerID: sourceOfferID },
                    },
                })
            )
        }
    }, [dispatch, sourceOfferID, activeTab, tabId, page])

    useEffect(() => {
        dispatch(clearTermForm())
        // Clear offer terms data when in add mode without copy to prevent showing stale data
        if (page === "add" && !copyFromID) {
            dispatch(clearOfferTermsConditions())
        }
    }, [dispatch, page, copyFromID])

    useEffect(() => {
        return () => {
            dispatch(clearOfferTermsConditions())
        }
    }, [dispatch])

    // Populate form data from fetched terms when copying
    useEffect(() => {
        if (copyFromID && page === "add" && offerTermsConditions.length > 0 && termFormData.length === 0) {
            offerTermsConditions.forEach(term => {
                const termText = typeof term === 'string' ? term : term.termText
                if (termText) {
                    dispatch(addTermToForm({ termText }))
                }
            })
        }
    }, [copyFromID, page, offerTermsConditions, termFormData.length, dispatch])

    const handleAddTermToForm = async () => {
        // Validate form before proceeding
        const isValid = await validateBeforeConfirm(validation)
        if (!isValid) {
            return
        }

        const newTerm = {
            termText: validation.values.termText.trim(),
        }

        if (editingTermIndex !== null) {
            dispatch(updateTermInForm(editingTermIndex, newTerm))
            setEditingTermIndex(null)
        } else {
            dispatch(addTermToForm(newTerm))
        }

        // Reset form
        setCurrentTerm({ termText: "" })
        validation.resetForm()
    }

    const handleEditTerm = (term, index) => {
        setCurrentTerm({
            termText: term.termText,
        })
        setEditingTermIndex(index)
    }

    const handleDeleteTermFromForm = (index) => {
        dispatch(removeTermFromForm(index))

        if (editingTermIndex === index) {
            setEditingTermIndex(null)
            setCurrentTerm({ termText: "" })
            validation.resetForm()
        }
    }

    const handleCancelEdit = () => {
        setEditingTermIndex(null)
        setCurrentTerm({ termText: "" })
        validation.resetForm()
    }

    const handleFinalSubmit = () => {
        if (isSubmitting) return // Prevent multiple submissions
        
        setIsSubmitting(true)

        const payload = {
            offerID: parseInt(offerID),
            termsConditions: termFormData.map(term => term.termText),
        }

        dispatch(manageOfferTermsConditions(payload, history))
    }

    // Handle success states
    useEffect(() => {
        if (termCreationSuccess && termCreationSuccess !== prevTermCreationSuccess.current) {
            setIsSubmitting(false)
            setIsEditMode(false)
            hasFetchedCopyData.current = false // Data saved, show saved listing now

            if (page !== "add") {
                setShowAddForm(false)
            }

            if (page === "add" && onTabCompletion) {
                onTabCompletion("5")
            }

            if (offerID) {
                dispatch(
                    getOfferTermsConditions({
                        headers: {
                            "input-parameters": { offerID: offerID },
                        },
                    })
                )
            }

            prevTermCreationSuccess.current = termCreationSuccess
        }
    }, [termCreationSuccess, page, offerID, dispatch, onTabCompletion])

    // Separate effect for error handling
    useEffect(() => {
        if (offerTermsConditionsError && offerTermsConditionsError !== prevOfferTermsConditionsError.current) {
            setIsSubmitting(false)
            prevOfferTermsConditionsError.current = offerTermsConditionsError
        }
    }, [offerTermsConditionsError])


    const handleAddNewTerm = () => {
        dispatch(clearTermForm())
        setCurrentTerm({ termText: "" })
        setEditingTermIndex(null)
        setIsEditMode(false)
        setShowAddForm(true)
        validation.resetForm()
    }

    const handleEditExistingTerms = () => {
        dispatch(clearTermForm())

        offerTermsConditions.forEach(term => {
            dispatch(addTermToForm({
                termText: term
            }))
        })

        setIsEditMode(true)
        setShowAddForm(true)
        setCurrentTerm({ termText: "" })
        setEditingTermIndex(null)
        validation.resetForm()
    }

    const handleCancelForm = () => {
        dispatch(clearTermForm())
        setCurrentTerm({ termText: "" })
        setEditingTermIndex(null)
        setIsEditMode(false)
        validation.resetForm()
        if (page !== "add") {
            setShowAddForm(false)
        }
    }


    // Handle input changes
    const handleInputChange = (field, value) => {
        setCurrentTerm(prev => ({
            ...prev,
            [field]: value
        }))
        validation.setFieldValue(field, value)
    }

    // Create a proper validation object for read-only fields
    const getValidationForReadOnly = () => ({
        errors: {},
        touched: {},
        values: {},
        setFieldValue: () => { },
        handleBlur: () => { },
        isValid: true,
    })

    const getHeaderButton = () => {
        if (page === "add" || showAddForm) {
            return null
        }

        if (!canEdit) {
            return null
        }

        if (offerTermsConditions.length === 0) {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_OFFER_TERMS_CONDITIONS}
                    actionType="post"
                >
                    <CButton.Success
                        icon="bx bx-plus"
                        text="Add Terms & Conditions"
                        onClick={handleAddNewTerm}
                    />
                </AccessControlWrapper>
            )
        } else {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_OFFER_TERMS_CONDITIONS}
                    actionType="post"
                >
                    <CButton.Primary
                        text="Edit Terms & Conditions"
                        onClick={handleEditExistingTerms}
                    />
                </AccessControlWrapper>
            )
        }
    }

    return (
        <>
            {/* Error Display */}
            {offerTermsConditionsError && shouldDisplayError(offerTermsConditionsError) && (
                <Alert color="danger" className="mb-3">
                    {offerTermsConditionsError.message || "An error occurred"}
                </Alert>
            )}

            {/* Add/Edit Term Form */}
            {showAddForm && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">
                            {isEditMode ? "Edit Terms & Conditions" : (termFormData.length > 0 ? "Manage Terms & Conditions" : "Add Terms & Conditions")}
                        </h6>

                        <Form
                            className="needs-validation"
                            onSubmit={e => {
                                e.preventDefault()
                                validation.handleSubmit()
                                return false
                            }}
                        >
                            {termFormData.map((term, index) => (
                                <Row key={index} className="align-items-end mb-2">
                                    <Col md="8">
                                        <TextInput
                                            name={`termText_${index}`}
                                            label="Term & Condition"
                                            placeholder="Enter term or condition text"
                                            value={editingTermIndex === index ? currentTerm.termText : term.termText}
                                            readOnly={editingTermIndex !== index || isSubmitting}
                                            maxLength={500}
                                            required={editingTermIndex === index}
                                            onChange={(e) => {
                                                if (editingTermIndex === index) {
                                                    handleInputChange('termText', e.target.value)
                                                }
                                            }}
                                            validation={editingTermIndex === index ? validation : getValidationForReadOnly()}
                                            inputClassName={editingTermIndex !== index ? "text-dark" : ""}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mb-2">
                                            {editingTermIndex === index ? (
                                                <>
                                                    <IconButton.Save
                                                        onClick={handleAddTermToForm}
                                                        title="Update term"
                                                        disabled={isSubmitting || !validation.isValid || !currentTerm.termText.trim()}
                                                    />
                                                    <IconButton.Cancel
                                                        onClick={handleCancelEdit}
                                                        title="Cancel edit"
                                                        disabled={isSubmitting}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleEditTerm(term, index)}
                                                        title="Edit term"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-edit"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteTermFromForm(index)}
                                                        title="Delete term"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-trash"></i>
                                                    </button>
                                                </>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            ))}

                            {editingTermIndex === null && (
                                <Row className="align-items-center mb-3">
                                    <Col md="8">
                                        <TextInput
                                            name="termText"
                                            label="Term & Condition"
                                            placeholder="Enter term or condition text"
                                            value={currentTerm.termText}
                                            readOnly={isSubmitting}
                                            maxLength={500}
                                            required={true}
                                            onChange={(e) => handleInputChange('termText', e.target.value)}
                                            validation={validation}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mt-3">
                                            <IconButton.Add
                                                onClick={handleAddTermToForm}
                                                disabled={isSubmitting || !validation.isValid || !currentTerm.termText.trim()}
                                                title="Add term"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </Form>

                        {/* Form Action Buttons */}
                        <div className="text-end">
                            {page === "detail" && (
                                <CButton.Secondary
                                    text="Cancel"
                                    className="me-2"
                                    onClick={handleCancelForm}
                                    disabled={isSubmitting}
                                />
                            )}

                            {(termFormData.length > 0 || isEditMode) && (
                                <ConfirmButton
                                    contextUri={url.MANAGE_OFFER_TERMS_CONDITIONS}
                                    actionType="post"
                                    validateBeforeConfirm={() =>
                                        Promise.resolve(isEditMode || termFormData.length > 0)
                                    }
                                    onConfirm={handleFinalSubmit}
                                    message={`Are you sure you want to ${isEditMode ? 'update' : 'save'} these terms & conditions?`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <CButton.Primary
                                            text={isEditMode ? "Update Terms & Conditions" : "Save Terms & Conditions"}
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                        />
                                    }
                                />
                            )}
                        </div>
                    </CardBody>
                </Card>
            )}

            {/* // Don't show saved listing when copying (data is from source offer, not saved yet) */}
            {!isEditMode && !hasFetchedCopyData.current && (
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Terms & Conditions</h5>
                            {getHeaderButton()}
                        </div>
                        
                        {isLoadingOfferTermsConditions ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : offerTermsConditions.length > 0 ? (
                            <ul className="ps-3">
                                {offerTermsConditions.map((term, index) => (
                                    <li key={index} className="py-1">
                                        {term}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-center py-4">
                                <i className="bx bx-info-circle display-1 text-muted"></i>
                                <p className="text-muted mt-3">No terms & conditions added yet</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </>
    )
}